import { ToastContainer, Link } from "@react-spectrum/s2";
import {
  Routes,
  Route,
  Navigate,
  useMatch,
  useLocation,
  useParams,
} from "react-router";
import { useStore } from "zustand";

import { store } from "../store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

import { usePageTracking } from "@app/hooks/usePageTracking";
import { useAlerts } from "@app/hooks/useAlerts";

import { Header } from "@app/components/Header";
import { BreadcrumbsProvider } from "@app/components/Breadcrumbs";
import { MobileHeader } from "@app/components/MobileHeader";
import { SideNav } from "@app/components/SideNav";
import { BackgroundVideo } from "@app/components/BackgroundVideo";
import { AlertBanner } from "@app/components/AlertBanner";
import { WelcomeView } from "./WelcomeView";
import { StoriesList } from "./stories/StoriesList";
import { Story } from "./story/Story";
import { TemplateList } from "./templates/TemplateList";
import { TemplateView } from "./templates/TemplateView";
import { ProjectsView } from "./projects/ProjectsView";
import { ProjectView } from "./projects/ProjectView";
import { VideoWizard } from "./projects/VideoWizard";

import { AdminList } from "./admin/AdminList";
import { MogrtAdmin } from "./admin/MogrtAdmin";
import { CropImage } from "./admin/CropImage";
import { TextToSpeech } from "./admin/TextToSpeech";
import { AdminUsers } from "./admin/AdminUsers";
import { AdminProjects } from "./admin/AdminProjects";
import { FluffyJaws } from "./admin/FluffyJaws";
import { AdminFootage } from "./admin/AdminFootage";
import { IntegrationsStatus } from "./admin/IntegrationsStatus";
import { PromptPlayground } from "./admin/PromptPlayground";

const darkVideoURL = new URL(
  "~/web-src/src/assets/BG_Video_Gradient.webm",
  import.meta.url,
).href;
const lightVideoURL = new URL(
  "~/web-src/src/assets/BG_Video_Light.webm",
  import.meta.url,
).href;

// Legacy `/videos/*` URLs (bookmarks, emails, shared links) now live under `/projects/*`.
const VideosRedirect = () => {
  const { "*": rest } = useParams();
  const { search, hash } = useLocation();
  const target = rest ? `/projects/${rest}` : "/projects";
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

export const Layout = () => {
  const isIndex = useMatch("/");
  const isStory = useMatch("/stories/:id");

  usePageTracking();

  const colorScheme = useStore(store, (state) => state.colorScheme);

  const videoURL = colorScheme === "dark" ? darkVideoURL : lightVideoURL;
  const padding = !isStory ? "default" : "none";

  const { data: alerts } = useAlerts();

  return (
    <BreadcrumbsProvider>
      <div
        className={style({
          display: "grid",
          gridTemplateAreas: {
            default: ["header", "content", "prompt", "."],
            lg: [
              "header header header header header",
              ". nav . content .",
              ". nav . prompt .",
              ". . . . .",
            ],
          },
          gridTemplateColumns: {
            default: "auto",
            lg: [0, "auto", 0, "minmax(0, 1fr)", 8],
          },
          gridTemplateRows: {
            default: {
              default: ["auto", "1fr", "auto", 0],
              isIndex: ["auto", "1fr", "auto", 16],
            },
            lg: {
              default: ["auto", "minmax(0, 1fr)", "auto", 0],
              isIndex: ["auto", "minmax(0, 1fr)", "auto", 16],
            },
          },
          justifyContent: "stretch",
          justifyItems: "stretch",
          // gap: 24,
          boxSizing: "border-box",
          width: "[100vw]",
          height: "[100vh]",
        })({ isIndex: false })}
      >
        <MobileHeader
          styles={style({
            gridArea: "header",
          })}
        />
        <Header
          styles={style({
            gridArea: "header",
            display: {
              default: "none",
              lg: "grid",
            },
            alignItems: "center",
            gap: 8,
            paddingY: 8,
            paddingStart: 8,
            paddingEnd: 24,
          })}
        />
        <SideNav
          styles={style({
            display: {
              default: "none",
              lg: "flex",
            },
            gridArea: "nav",
            boxSizing: "border-box",
            paddingY: 16,
            marginX: 12,
          })}
        />
        <div
          id="beStory-content"
          className={style({
            gridArea: "content",
            overflowY: "auto",
            maxWidth: "[100vw]",
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            backgroundColor: "base",
            borderTopRadius: "lg",
            borderBottomRadius: "none",
            boxShadow: "emphasized",
            position: "relative",
            zIndex: 0,
          })}
        >
          {!!alerts?.length && (
            <div className={style({ position: "sticky", top: 0, zIndex: 1 })}>
              {alerts?.map((alert, index) => (
                <AlertBanner key={index} variant={alert.variant}>
                  {alert.message}
                </AlertBanner>
              ))}
            </div>
          )}
          <div
            className={style({
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              paddingX: {
                padding: {
                  default: 24,
                  none: 0,
                },
              },
              position: "relative",
              zIndex: 0,
            })({ padding })}
          >
            <BackgroundVideo
              src={videoURL}
              variant={isIndex ? "default" : "faded"}
            />
            <div
              className={style({
                boxSizing: "border-box",
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: 0,
                minHeight: 0,
                minHeight: 0,
                size: "full",
                // maxWidth: 1600,
                marginX: "auto",
                paddingY: 24,
                display: "flex",
                flexDirection: "column",
                zIndex: 1,
              })}
            >
              <Routes>
                <Route index element={<WelcomeView />} />

                <Route path="stories">
                  <Route index element={<StoriesList />} />
                  <Route
                    path="new/:id?/:step?"
                    element={<VideoWizard type={["html"]} />}
                  />
                  <Route path=":id" element={<Story />} />
                </Route>

                <Route path="templates">
                  <Route index element={<TemplateList />} />
                  <Route path=":id" element={<TemplateView />} />
                </Route>

                <Route path="projects">
                  <Route index element={<ProjectsView />} />
                  <Route path="user/:email" element={<ProjectsView />} />
                  <Route
                    path="composer/:id?/:step?"
                    element={<VideoWizard type="modular" />}
                  />
                  <Route
                    path="new/:id?/:step?"
                    element={<VideoWizard type={["express", "mogrt"]} />}
                  />
                  <Route path=":id" element={<ProjectView />} />
                </Route>

                <Route path="videos/*" element={<VideosRedirect />} />

                <Route path="admin">
                  <Route index element={<AdminList />} />
                  <Route path="prompts" element={<PromptPlayground />} />
                  <Route path="mogrt" element={<MogrtAdmin />} />
                  <Route path="text-to-speech" element={<TextToSpeech />} />
                  <Route path="image-crop" element={<CropImage />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route
                    path="videos"
                    element={<Navigate to="/admin/projects" replace />}
                  />
                  <Route path="fluffyjaws" element={<FluffyJaws />} />
                  <Route path="footage" element={<AdminFootage />} />
                  <Route path="integrations" element={<IntegrationsStatus />} />
                </Route>

                {/* Fallback route for any unmatched paths */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer placement="bottom center" />
    </BreadcrumbsProvider>
  );
};
